import { ClickAwayListener } from '@mui/base';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Chip,
  Grid,
  Stack,
  Typography,
  Box,
  IconButton,
  Button,
  Tooltip,
} from '@mui/material';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Editor as TinyMCEEditor } from 'tinymce';
import CloseIcon from '@mui/icons-material/Close';
import { ApplicationNote } from '@features/applicationNotes/types';
import { darkBlue, lightBlue, white } from '@styles/colors';
import { bold } from '@styles/fontWeights';
import { useGetEnterpriseApplicationNotes } from '@features/applicationNotes/hooks/useGetEnterpriseApplicationNotes';
import { useCreateEnterpriseApplicationNote } from '@features/applicationNotes/hooks/useCreateEnterpriseApplicationNote';
import { useUpdateEnterpriseApplicationNote } from '@features/applicationNotes/hooks/useUpdateEnterpriseApplicationNote';
import CreateEnterpriseNoteModal from '../CreateEnterpriseNoteModal/CreateEnterpriseNoteModal';
import DeleteEnterpriseNoteModal from '../DeleteEnterpriseNoteModal/DeleteEnterpriseNoteModal';
import { useCreateEnterpriseSavedMessage } from '@features/savedMessages/hooks/useCreateEnterpriseSavedMessage';
import { Close, HelpOutline, Info } from '@mui/icons-material';
import { toast } from 'react-toastify';
import SaveIcon from '@mui/icons-material/Save';
import BundledEditor from '@utils/BundledEditor';
import AssistantIcon from '@mui/icons-material/Assistant';
import AIPromptModal from '@features/messaging/components/AIPromptModal/AIPromptModal';
import { AITooltipText } from '@features/messaging';

type EnterpriseApplicationNotesProps = {
  applicationId: number;
  companyId: number;
  handleClose: () => void;
};

// Notes from this array cannot be renamed or deleted
const defaultNotes = ['General'];
const DEBOUNCE_DELAY = 500;

const EnterpriseApplicationNotes: FC<EnterpriseApplicationNotesProps> = ({
  applicationId,
  companyId,
  handleClose,
}) => {
  const [message, setMessage] = useState('');
  const [errorText, setErrorText] = useState('');
  const [selectedNote, setSelectedNote] = useState<ApplicationNote | null>(
    null,
  );
  const [showAiPromptModal, setShowAiPromptModal] = useState(false);
  const [showNotesAlert, setShowNotesAlert] = useState(true);
  const [isCreateNoteModalOpen, setIsCreateNoteModalOpen] = useState(false);
  const [isDeleteNoteModalOpen, setIsDeleteNoteModalOpen] = useState(false);

  const { enterpriseApplicationNotesData } = useGetEnterpriseApplicationNotes({
    applicationId,
    companyId,
    setErrorText,
  });

  const { updateNote, isUpdateNoteLoading } =
    useUpdateEnterpriseApplicationNote({
      applicationId,
      companyId,
      setErrorText,
    });
  const { createNote, isCreateNoteLoading } =
    useCreateEnterpriseApplicationNote({
      company_id: companyId,
      setErrorText,
    });

  const { createEnterpriseSavedMessage, isCreatingEnterpriseSavedMessage } =
    useCreateEnterpriseSavedMessage({
      setErrorText,
    });

  const editorRef = useRef<TinyMCEEditor | null>(null);

  const handleSave = useCallback(() => {
    if (selectedNote) {
      if (selectedNote?.id) {
        updateNote({
          name: selectedNote?.name,
          note: message,
          company_id: companyId,
          application_note_id: selectedNote.id,
        });
      } else {
        createNote({
          application_id: applicationId,
          name: selectedNote.name,
          company_id: companyId,
          note: message,
        });
      }
    }
  }, [
    message,
    selectedNote,
    applicationId,
    updateNote,
    createNote,
    handleClose,
  ]);

  const handleDeleteNote = () => setIsDeleteNoteModalOpen(true);

  const handleAwayClick = (event: MouseEvent | TouchEvent) => {
    const target = event.target as HTMLElement;
    const modalContainer = document.getElementsByClassName('tox-dialog')[0];

    if (modalContainer && modalContainer.contains(target)) {
      return;
    }

    handleClose();
  };

  const handleInsertAIContent = (content: string) => {
    setMessage(content);
    setShowAiPromptModal(false);
  };

  const handleSaveAsTemplate = (label: any, content: any) => {
    if (!content) {
      setErrorText('Please enter content for your template');
      return;
    }
    const options = {
      onSuccess: () => {
        handleClose();
        toast.success('Note saved as template');
      },
    };

    createEnterpriseSavedMessage(
      {
        label,
        content,
        is_note: true,
        company_id: companyId,
      },
      options,
    );
  };

  useEffect(() => {
    if (enterpriseApplicationNotesData?.length && !selectedNote?.id) {
      const isSelectedNoteExist = enterpriseApplicationNotesData.find(
        (note: ApplicationNote) => note.name === selectedNote?.name,
      );

      if (!isSelectedNoteExist) {
        setSelectedNote(enterpriseApplicationNotesData[0] as ApplicationNote);
        setMessage(enterpriseApplicationNotesData[0].note);
        return;
      }
      const newSelectedNote = isSelectedNoteExist;

      setSelectedNote(newSelectedNote as ApplicationNote);
      setMessage(newSelectedNote?.note);
    }
  }, [enterpriseApplicationNotesData, selectedNote]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (message.trim()) {
        handleSave();
      }
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(debounceTimer); // Cleanup on unmount or message change
  }, [message, handleSave]);

  return (
    <ClickAwayListener onClickAway={handleAwayClick}>
      <Stack
        sx={{ position: 'relative' }}
        p={4}
        borderRadius="8px"
        bgcolor={darkBlue}
        spacing={3}
      >
        <IconButton
          sx={{ position: 'absolute', right: '4px', top: '0', color: white }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
        {errorText && <Alert severity="error">{errorText}</Alert>}
        <Grid container alignItems="center">
          <Grid item xs={12}>
            <Stack
              bgcolor={white}
              alignItems="center"
              p={2}
              borderRadius="5px"
              direction="row"
              justifyContent="space-between"
            >
              <Stack alignItems="center" direction="row" spacing={3}>
                <Typography color={darkBlue} variant="h4" fontWeight={bold}>
                  Notes:
                </Typography>
                <Box
                  style={{
                    overflow: 'auto',
                    marginLeft: '5px',
                    display: 'flex',
                  }}
                >
                  {enterpriseApplicationNotesData &&
                    enterpriseApplicationNotesData
                      ?.sort(
                        (
                          a: { name: string | number },
                          b: { name: string | number },
                        ) => {
                          const order: Record<string, number> = {
                            General: 1,
                            Interview: 2,
                          };

                          const rankA = order[a.name as string] || 3;
                          const rankB = order[b.name as string] || 3;

                          return rankA - rankB;
                        },
                      )
                      ?.map((note: ApplicationNote) => (
                        <Chip
                          key={note.name}
                          variant={
                            selectedNote?.id
                              ? note.id === selectedNote.id
                                ? 'filled'
                                : 'outlined'
                              : note.name === selectedNote?.name
                                ? 'filled'
                                : 'outlined'
                          }
                          color="secondary"
                          onClick={() => {
                            setSelectedNote(note);
                            setMessage(note.note);
                          }}
                          label={note.name}
                          onDelete={
                            defaultNotes.includes(note.name)
                              ? undefined
                              : () => {
                                  setSelectedNote(note);
                                  handleDeleteNote();
                                }
                          }
                          sx={{
                            borderRadius: 5,
                            fontSize: '14px',
                            lineHeight: '18px',
                            height: '26px',
                            '& .MuiChip-deleteIcon': {
                              fontSize: '11px',
                              '&:hover': {
                                color: 'red',
                              },
                            },
                          }}
                          style={{
                            marginLeft: '10px',
                          }}
                          deleteIcon={
                            <CloseIcon style={{ fontSize: '12px' }} />
                          }
                        />
                      ))}
                </Box>
                <Chip
                  variant="filled"
                  color="secondary"
                  onClick={() => setIsCreateNoteModalOpen(true)}
                  label="+ Add Note"
                  style={{
                    borderRadius: 5,
                    fontSize: '13px',
                    lineHeight: '18px',
                    height: '26px',
                    marginLeft: '10px',
                    fontWeight: 'bold',
                  }}
                />
              </Stack>
              <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
                <LoadingButton
                  variant="contained"
                  sx={{
                    width: 'fit-content',
                  }}
                  startIcon={<SaveIcon />}
                  fullWidth
                  onClick={() =>
                    handleSaveAsTemplate(selectedNote?.name, message)
                  }
                  loading={isCreatingEnterpriseSavedMessage}
                >
                  Save Note as Template
                </LoadingButton>
                <Stack spacing={2} direction="row" alignItems="center">
                  <Button
                    variant="outlined"
                    onClick={() => setShowAiPromptModal(true)}
                    startIcon={<AssistantIcon />}
                  >
                    Write with AI
                  </Button>
                  <Tooltip title={AITooltipText} placement="top" arrow>
                    <HelpOutline color="secondary" fontSize="small" />
                  </Tooltip>
                </Stack>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
        <BundledEditor
          onInit={(evt: any, editor: TinyMCEEditor | null) =>
            (editorRef.current = editor)
          }
          value={message}
          onEditorChange={(newValue: string) => setMessage(newValue)}
          init={{
            height: 250,
            menubar: false,
            plugins: ['lists', 'autolink', 'link', 'pagebreak'],
            toolbar:
              'spellchecker language spellcheckdialog | styles | bold italic | bullist numlist | link',
            placeholder: 'Add note',
            spellchecker_dialog: true,
            spellchecker_rpc_url: 'localhost/ephox-spelling',
            spellchecker_language: 'en_US',
            spellchecker_active: true,
            browser_spellcheck: true,
            contextmenu: false,
          }}
          onClick={(e: { stopPropagation: () => any }) => e.stopPropagation()}
        />
        {showNotesAlert ? (
          <Box
            sx={{
              width: '100%',
              backgroundColor: 'rgb(213, 240, 250)',
              color: 'rgb(28, 108, 139)',
              borderRadius: 5,
              p: 2,
              boxShadow: 1,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Info />
                <Typography variant="body2">
                  Notes are private and will not be seen by anyone, including
                  the applicant, except you and your team, and save
                  automatically.
                </Typography>
              </Box>
              <IconButton
                onClick={() => setShowNotesAlert(false)}
                sx={{ color: 'inherit', p: 0.5 }}
              >
                <Close />
              </IconButton>
            </Stack>
          </Box>
        ) : null}
        <CreateEnterpriseNoteModal
          isOpen={isCreateNoteModalOpen}
          handleClose={() => setIsCreateNoteModalOpen(false)}
          applicationId={applicationId}
          companyId={companyId}
        />
        <DeleteEnterpriseNoteModal
          isOpen={isDeleteNoteModalOpen}
          handleClose={() => setIsDeleteNoteModalOpen(false)}
          applicationId={applicationId}
          company_id={companyId}
          applicationNoteId={selectedNote?.id || null}
          clearSelectedNote={() => setSelectedNote(null)}
        />
        {showAiPromptModal && (
          <AIPromptModal
            isOpen={showAiPromptModal}
            handleInsertAIContent={handleInsertAIContent}
            handleClose={() => setShowAiPromptModal(false)}
          />
        )}
      </Stack>
    </ClickAwayListener>
  );
};

export default EnterpriseApplicationNotes;
